/**
 * Bio Link Template - Elegant Black Bio Link Design
 * Features Cloudflare banner and sophisticated black aesthetic
 * Supports both English (US) and Spanish (Spain) content.
 *
 * Instagram (Mejores Amigos) and Join Telegram use MuxVideoPlayerWithCTA
 * fullscreen reel — no PaymentModal. Each option has its own video + CTA (key).
 */

import {
  BRAND_NAME,
  BRAND_PERSON_NAME,
  BRAND_INSTAGRAM_URL,
  BRAND_TIKTOK_URL,
} from "@/lib/brandConfig";
import { HEADER_PROFILE_IMAGE_URL } from "@/config/cloudflare";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
// Amplitude tracking removed to prevent React error #310
import { trackButtonClickGA, trackTemplateUsageGA } from "@/lib/analytics";
import {
  trackContactMeta,
  trackLeadMeta,
  trackViewContentMeta,
} from "@/lib/metaPixelAnalytics";
import {
  trackButtonClickPostHog,
  trackConversionPostHog,
  trackLightboxClosedPostHog,
  trackLightboxNavigationPostHog,
  trackLightboxOpenedPostHog,
  trackSocialClickPostHog,
  trackTemplateUsagePostHog,
  trackVideoCompletePostHog,
  trackVideoPlayPostHog,
  trackVideoProgressPostHog,
} from "@/lib/posthogAnalytics";
// BioLink conversion tracking removed
// Background images from Cloudflare for desktop and mobile
import {
  getBioTemplateBackgroundStyle,
  preloadBioTemplateBackgrounds,
} from "@/lib/backgroundImages";
import {
  applyInstagramBrowserCSS,
  getInstagramOptimizations,
  trackInstagramBrowserUsage,
} from "@/lib/instagramBrowserDetection";
import { Instagram } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";

// Lazy load heavy components to improve initial load time
const MuxVideoSurface = lazy(() => import("@/components/MuxVideoSurface"));
const MuxVideoPlayerWithCTA = lazy(
  () => import("@/components/MuxVideoPlayerWithCTA"),
);
// Lazy load all icons to reduce initial bundle size
const ChevronLeft = lazy(() =>
  import("lucide-react").then((module) => ({ default: module.ChevronLeft })),
);
const ChevronRight = lazy(() =>
  import("lucide-react").then((module) => ({ default: module.ChevronRight })),
);
const X = lazy(() =>
  import("lucide-react").then((module) => ({ default: module.X })),
);

import {
  getTelegramCTAUrlByCountry,
  getTelegramPlaybackIdByCountry,
} from "@/config/telegramGroupsVideo";
import { getBaseLanguage } from "@/lib/i18n";
import type { VideoCTAModalConfig } from "@/components/MuxVideoPlayerWithCTA";
import type { VideoActionButton } from "@/components/MuxVideoPlayer";

export interface TemplateProps {
  mediaItems: Array<{
    id: number;
    type: "video" | "photo";
    src: string;
    title: string;
    playbackId?: string; // Mux playback ID (required for videos in BioLinkTemplate)
    cloudflareId?: string;
    embedId?: string;
    actionButton?: VideoActionButton; // Optional: URL de destino para el botón del video
  }>;
}

export const BioLinkTemplate: React.FC<TemplateProps> = ({ mediaItems }) => {
  // Critical hooks first for fastest render - minimal dependencies
  const instagramOpts = getInstagramOptimizations();

  // Defer heavy template config until after initial render
  const templateConfig = useTemplateSelection();

  // Defer device detection to avoid blocking initial render
  const [deviceInfo, setDeviceInfo] = useState<{ isMobile: boolean }>({
    isMobile: false,
  });

  // Optimized state initialization
  const [selectedMedia, setSelectedMedia] = useState<
    (typeof mediaItems)[0] | null
  >(null);
  const [videoCTAOverlay, setVideoCTAOverlay] = useState<{
    isOpen: boolean;
    config: VideoCTAModalConfig | null;
  }>({ isOpen: false, config: null });
  const [videoCTAOverlayMounted, setVideoCTAOverlayMounted] = useState(false);

  // Performance refs
  const lightboxOpenTimeRef = useRef<number | null>(null);
  const lightboxViewedCountRef = useRef<number>(1);
  const imageLoadTimeRef = useRef<Record<string, number>>({});
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const videoProgressTrackedRef = useRef<Set<number>>(new Set());

  // Track lightbox opened and set timer when selectedMedia is first set (not on nav)
  const lightboxWasOpenRef = useRef(false);
  const countryCode = templateConfig.country || undefined;
  useEffect(() => {
    if (!selectedMedia) {
      lightboxWasOpenRef.current = false;
      return;
    }
    lightboxOpenTimeRef.current = Date.now();
    if (!lightboxWasOpenRef.current) {
      lightboxViewedCountRef.current = 1;
      trackLightboxOpenedPostHog(
        selectedMedia.id,
        "gallery_click",
        selectedMedia.type,
        "BioLinkTemplate",
        countryCode,
      );
      lightboxWasOpenRef.current = true;
    }
  }, [selectedMedia, countryCode]);

  // Block body scroll when lightbox is open (especially important for Instagram browser)
  useEffect(() => {
    if (selectedMedia) {
      // Store original values
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;

      // Block scroll - especially important in Instagram browser
      if (instagramOpts.isInstagramBrowser) {
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
        document.body.style.height = "100%";
      }
      document.body.style.overflow = "hidden";

      // Cleanup function to restore scroll
      return () => {
        document.body.style.overflow = originalOverflow;
        if (instagramOpts.isInstagramBrowser) {
          document.body.style.position = originalPosition;
          document.body.style.width = originalWidth;
          document.body.style.height = originalHeight;
        }
      };
    }
  }, [selectedMedia, instagramOpts.isInstagramBrowser]);

  // Block body scroll when video CTA overlay (fullscreen reel) is open — better UX, no background scroll
  useEffect(() => {
    if (!videoCTAOverlay.isOpen) return;
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    const originalHeight = document.body.style.height;
    if (instagramOpts.isInstagramBrowser) {
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.height = "100%";
    }
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
      if (instagramOpts.isInstagramBrowser) {
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.height = originalHeight;
      }
    };
  }, [videoCTAOverlay.isOpen, instagramOpts.isInstagramBrowser]);

  useEffect(() => {
    if (!videoCTAOverlay.isOpen) {
      setVideoCTAOverlayMounted(false);
      return;
    }
    const id = requestAnimationFrame(() => setVideoCTAOverlayMounted(true));
    return () => cancelAnimationFrame(id);
  }, [videoCTAOverlay.isOpen]);

  // Control body scroll - NO scroll in any device, fit everything to viewport
  useEffect(() => {
    // Store original overflow
    const originalOverflow = document.body.style.overflow;
    const originalPosition = document.body.style.position;
    const originalWidth = document.body.style.width;
    const originalHeight = document.body.style.height;

    // No scroll - fit to viewport
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.height = "100%";

    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.position = originalPosition;
      document.body.style.width = originalWidth;
      document.body.style.height = originalHeight;
    };
  }, []);

  // Background images are now preloaded in index.html for better LCP
  // This effect is kept as fallback only
  useEffect(() => {
    // Only run fallback preload if images weren't preloaded in HTML
    const hasPreloads =
      document.querySelectorAll('link[rel="preload"][as="image"]').length > 0;
    if (!hasPreloads && window.requestIdleCallback) {
      requestIdleCallback(
        () => {
          preloadBioTemplateBackgrounds();
        },
        { timeout: 3000 },
      );
    }
  }, []);

  // Simplified button visibility - removed expensive MutationObserver
  // Buttons now use inline styles directly, no need for DOM manipulation

  // Optimized device detection - only once on mount, no resize listeners
  useEffect(() => {
    if (typeof window !== "undefined") {
      const isMobile =
        window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent,
        );
      setDeviceInfo({ isMobile });
    }
  }, []);

  // Social links - icons lazy loaded
  const socialLinks = [
    // All social links removed from BioLinkTemplate
  ];

  // Navigation functions for lightbox
  const navigateToPrevious = () => {
    if (!selectedMedia) return;
    const currentIndex = mediaItems.findIndex(
      (item) => item.id === selectedMedia.id,
    );
    const previousIndex =
      currentIndex > 0 ? currentIndex - 1 : mediaItems.length - 1;
    const previousItem = mediaItems[previousIndex];

    trackLightboxNavigationPostHog(
      "prev",
      selectedMedia.id,
      previousItem.id,
      "BioLinkTemplate",
      templateConfig.country || undefined,
    );
    lightboxViewedCountRef.current += 1;
    setSelectedMedia(previousItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  const navigateToNext = () => {
    if (!selectedMedia) return;
    const currentIndex = mediaItems.findIndex(
      (item) => item.id === selectedMedia.id,
    );
    const nextIndex =
      currentIndex < mediaItems.length - 1 ? currentIndex + 1 : 0;
    const nextItem = mediaItems[nextIndex];

    trackLightboxNavigationPostHog(
      "next",
      selectedMedia.id,
      nextItem.id,
      "BioLinkTemplate",
      templateConfig.country || undefined,
    );
    lightboxViewedCountRef.current += 1;
    setSelectedMedia(nextItem);
    lightboxOpenTimeRef.current = Date.now();
  };

  // Swipe gesture state for Instagram-like navigation
  const swipeStartRef = useRef<{ x: number; y: number; time: number } | null>(
    null,
  );
  const SWIPE_THRESHOLD = 50; // Minimum distance for swipe
  const SWIPE_VELOCITY_THRESHOLD = 0.3; // Minimum velocity for swipe

  // Defer analytics tracking to avoid blocking initial render - More aggressive deferral
  useEffect(() => {
    // Use requestIdleCallback for non-critical analytics - Defer significantly to improve FCP/LCP
    const trackAnalytics = () => {
      // Track template load with detailed layout information
      const sectionName = templateConfig.isUSBio
        ? "US Bio Link"
        : "Spain Bio Link";
      const layoutName = templateConfig.isUSBio
        ? "elegant_black_layout"
        : "elegant_black_layout_spain";

      // Apply Instagram browser optimizations
      applyInstagramBrowserCSS();

      // Track Instagram browser usage
      trackInstagramBrowserUsage(
        "BioLinkTemplate",
        templateConfig.country || "UNKNOWN",
      );

      // Enhanced template tracking for both GA and Amplitude
      trackTemplateUsageGA(
        "BioLinkTemplate",
        templateConfig.isUSBio ? "US" : "LATAM",
        [
          "bio_link",
          "mux_video_cta",
          "cloudflare_images",
          "mobile_optimized",
          ...(templateConfig.country === "ES" ? ["gdpr_compliant"] : []),
        ],
      );

      // PostHog template tracking
      trackTemplateUsagePostHog(
        "BioLinkTemplate",
        templateConfig.isUSBio ? "US" : "LATAM",
        [
          "bio_link",
          "mux_video_cta",
          "cloudflare_images",
          "mobile_optimized",
          ...(templateConfig.country === "ES" ? ["gdpr_compliant"] : []),
        ],
      );

      // Meta Pixel: ViewContent – usuario vio el BioLink template
      trackViewContentMeta({
        content_type: "product",
        content_name: "BioLinkTemplate",
        content_ids: ["biolink_template"],
        currency: "USD",
      });

      // Enhanced country tracking for BioLink template
      const timezone = templateConfig.timezone;
      const browserLang = templateConfig.browserLang;
      const countryCode = templateConfig.country || "LATAM";

      // Track template load with country information
      if (typeof window !== "undefined" && window.gtag) {
        window.gtag("event", "template_load", {
          event_category: "Template Usage",
          event_label: `BioLinkTemplate_${countryCode || "UNKNOWN"}`,
          template_name: "BioLinkTemplate",
          country_code: countryCode,
          region: templateConfig.isUSBio ? "US" : "LATAM",
          timezone: timezone,
          language: browserLang,
          is_biolink_template: true,
        });
      }
    };

    // Defer analytics to idle time - Very aggressive deferral to improve FCP/LCP
    // Analytics should not block critical rendering
    if (window.requestIdleCallback) {
      window.requestIdleCallback(trackAnalytics, { timeout: 5000 });
    } else {
      setTimeout(trackAnalytics, 2000);
    }

    // Track time on page when user leaves
    const startTime = Date.now();

    const handleBeforeUnload = () => {
      const timeSpent = Math.round((Date.now() - startTime) / 1000);
      // Amplitude tracking removed
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handleBeforeUnload);

    // Defer scroll tracking to avoid blocking main thread
    let scrollTimeout: NodeJS.Timeout;
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const scrollPercent = Math.round(
          (window.scrollY / (document.body.scrollHeight - window.innerHeight)) *
            100,
        );
        // Amplitude scroll tracking removed
      }, 100); // Throttle scroll events
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handleBeforeUnload);
    };
  }, [templateConfig]);

  // Exit intent tracking
  useEffect(() => {
    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 0) {
        // Amplitude tracking removed
      }
    };

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, []);

  // Add performance optimizations
  useEffect(() => {
    // Polyfill for requestIdleCallback
    if (!window.requestIdleCallback) {
      window.requestIdleCallback = (callback: IdleRequestCallback) => {
        const timeoutId = setTimeout(
          () => callback({ didTimeout: false, timeRemaining: () => 50 }),
          1,
        );
        return timeoutId as unknown as number;
      };
    }

    // Profile image is now preloaded in index.html for better LCP
    // This is kept as fallback only - check if already preloaded
    let preloadLink: HTMLLinkElement | null = null;
    const existingPreload = document.querySelector(
      `link[href="${HEADER_PROFILE_IMAGE_URL}"]`,
    );
    if (!existingPreload) {
      // Fallback: only add if not already in HTML
      preloadLink = document.createElement("link");
      preloadLink.rel = "preload";
      preloadLink.href = HEADER_PROFILE_IMAGE_URL;
      preloadLink.as = "image";
      preloadLink.fetchPriority = "high";
      // NOTE: crossOrigin omitted — imagedelivery.net (Cloudflare) doesn't emit
      // CORS headers for anonymous-origin requests, which causes a CORS error
      // that blocks the preload and defeats the purpose of the hint entirely.
      document.head.appendChild(preloadLink);
    }

    // Optimize Instagram browser performance
    if (instagramOpts.isInstagramBrowser) {
      document.body.classList.add("instagram-browser-optimized");
    }

    // Optimized minimal styles for performance
    const style = document.createElement("style");
    style.textContent = `
      .bio-link-button {
        transform: translateZ(0);
        backface-visibility: hidden;
      }
      .instagram-browser-optimized {
        -webkit-overflow-scrolling: touch;
      }
      .instagram-browser-optimized * {
        -webkit-tap-highlight-color: transparent;
      }
      /* Lightbox scroll fix for Instagram browser */
      .instagram-browser-modal {
        touch-action: pan-y pinch-zoom !important;
      }
      .instagram-browser-modal > div[class*="relative w-full h-full"] {
        touch-action: pan-y pinch-zoom !important;
        -webkit-overflow-scrolling: touch !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (preloadLink && document.head.contains(preloadLink)) {
        document.head.removeChild(preloadLink);
      }
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
      document.body.classList.remove("instagram-browser-optimized");
    };
  }, [instagramOpts.isInstagramBrowser]);

  // Early return if Argentina is detected - AFTER all hooks are called
  if (templateConfig.country === "AR") {
    return null;
  }

  // Update background style when device type changes
  const currentBackgroundStyle = getBioTemplateBackgroundStyle(
    templateConfig.country,
    deviceInfo.isMobile ? "mobile" : "desktop",
  );

  return (
    <div
      className={`text-white ${instagramOpts.cssClasses.container} h-screen overflow-hidden`}
      style={{
        ...currentBackgroundStyle,
        // Optimize background loading
        willChange: "auto",
        contentVisibility: "auto",
        // No scroll - fit everything to viewport
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      }}
    >
      {/* Full overlay covering entire background image for better content readability */}
      <div className="fixed inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/80 pointer-events-none z-0" />

      {/* Minimalist Elegant Bio Link Layout */}
      <main className="relative z-10 h-full flex flex-col font-body" role="main">
        {/* Bio Link Content - Stacked vertically, no scroll, proportional */}
        <div
          className="relative flex flex-col items-center justify-between px-4 sm:px-6 py-6 sm:py-8 h-full"
          style={{
            minHeight: 0, // Allow flex shrink
          }}
        >
          {/* Content - Ultra minimal - Stacked - Proportional spacing */}
          <div className="relative z-10 w-full max-w-sm sm:max-w-md mx-auto text-center flex flex-col gap-4 sm:gap-6 flex-shrink">
            {/* Profile Section */}
            <div className="flex flex-col gap-3 sm:gap-4 flex-shrink-0">
              {/* Profile Image - Cloudflare Hosted - Compact */}
              <div className="relative mx-auto flex-shrink-0">
                <div className="bio-profile w-20 h-20 sm:w-24 sm:h-24 mx-auto rounded-full overflow-hidden">
                  <div className="w-full h-full rounded-full overflow-hidden">
                    <img
                      src={HEADER_PROFILE_IMAGE_URL}
                      alt={
                        templateConfig.isUSBio
                          ? `${BRAND_PERSON_NAME} - Premium Content Creator Profile Photo | Join My Exclusive Community`
                          : `${BRAND_PERSON_NAME} - Creadora de Contenido Premium | Únete a Mi Comunidad Exclusiva`
                      }
                      className="w-full h-full object-cover rounded-full"
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      width="128"
                      height="128"
                      style={{
                        contentVisibility: "auto",
                        // Optimize for LCP
                        willChange: "auto",
                      }}
                      onLoad={(e) => {
                        const loadTime =
                          performance.now() -
                          (imageLoadTimeRef.current["profile"] || 0);
                        // Amplitude tracking removed
                      }}
                      onLoadStart={() => {
                        imageLoadTimeRef.current["profile"] = performance.now();
                      }}
                      onError={(e) => {
                        const target = e.target as HTMLElement;
                        target.style.display = "none";
                        const fallback =
                          target.parentElement?.querySelector(
                            ".fallback-initials",
                          );
                        if (fallback) {
                          (fallback as HTMLElement).style.display = "flex";
                        }
                      }}
                    />
                    {/* Fallback initials */}
                    <div className="fallback-initials w-full h-full hidden items-center justify-center">
                      <span className="font-display text-3xl font-semibold text-white">DO</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Name & Badge - Compact */}
              <div className="flex flex-col gap-2 flex-shrink-0">
                <h2 className="font-display text-xl sm:text-2xl font-semibold text-white tracking-tight">
                  {BRAND_PERSON_NAME}
                </h2>
                <div className="inline-flex items-center px-3 py-1 border border-white/20 rounded-full mx-auto">
                  <span className="text-xs font-medium tracking-wide text-white/75">
                    {templateConfig.isUSBio
                      ? "Join My Community"
                      : "Únete a Mi Comunidad"}
                  </span>
                </div>
              </div>

              {/* Page Title */}
              <h1 className="sr-only">
                {templateConfig.isUSBio
                  ? `${BRAND_PERSON_NAME} — model and social content creator | Buenos Aires (CABA)`
                  : `${BRAND_PERSON_NAME} — modelo y creadora de contenido para redes | Comunidad exclusiva`}
              </h1>

              {/* Description - Compact */}
              <div className="max-w-sm mx-auto px-2 sm:px-0 flex-shrink">
                <p className="text-sm text-white/70 font-light leading-relaxed line-clamp-4 lining-nums tabular-nums">
                  {templateConfig.isUSBio
                    ? `I'm ${BRAND_PERSON_NAME.split(/\s+/)[0] ?? BRAND_PERSON_NAME} — model and social content creator, from Buenos Aires (CABA). Join for exclusive updates, campaigns and a closer look at my work on social. ✨`
                    : `Soy ${BRAND_PERSON_NAME.split(/\s+/)[0] ?? BRAND_PERSON_NAME}: modelo y creadora de contenido para redes, Buenos Aires (CABA). Unite para novedades, campañas y un vistazo más cercano a lo que comparto. ✨`}
                </p>
              </div>
            </div>

            {/* Main Action Buttons - Compact */}
            <div
              className={`flex flex-col gap-3 px-2 sm:px-0 flex-shrink-0 ${instagramOpts.cssClasses.buttons}`}
            >
              {/* Telegram Button - Join to my Group */}
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  const buttonText = templateConfig.isUSBio
                    ? "EXCLUSIVE MEMBER"
                    : "MIEMBRO EXCLUSIVO";
                  const trackingId = templateConfig.isUSBio
                    ? "biolink-telegram"
                    : "biolink-telegram-latam";
                  const countryCode = templateConfig.country || "LATAM";

                  // Get country-specific configuration
                  const telegramPlaybackId = getTelegramPlaybackIdByCountry(countryCode);
                  const telegramCtaUrl = getTelegramCTAUrlByCountry(countryCode);

                  trackButtonClickGA(
                    buttonText,
                    trackingId,
                    "telegram_video_open",
                    "telegram",
                    countryCode,
                    "BioLinkTemplate",
                  );
                  trackButtonClickPostHog(
                    buttonText,
                    trackingId,
                    "telegram_video_open",
                    "telegram",
                    countryCode,
                    "BioLinkTemplate",
                  );
                  trackConversionPostHog("video_open", "telegram");
                  trackLeadMeta({ content_name: "telegram_video_open" });

                  // Fullscreen reel (MuxVideoPlayerWithCTA) — channel_invite con config por país.
                  setVideoCTAOverlay({
                    isOpen: true,
                    config: {
                      service: "channel_invite",
                      variant: "fullscreen",
                      playbackId: telegramPlaybackId,
                      ctaUrl: telegramCtaUrl,
                      ctaLabel: templateConfig.isUSBio
                        ? "Join"
                        : "Unirte",
                      countryCode: countryCode,
                      language: getBaseLanguage(templateConfig.language || "es"),
                      openInNewTab: true,
                      displayMode: "reel",
                      loop: true,
                      onCTAClick: (url) => {
                        trackButtonClickGA(
                          buttonText,
                          trackingId,
                          "telegram_cta_click",
                          "telegram",
                          countryCode,
                          "BioLinkTemplate",
                        );
                        trackButtonClickPostHog(
                          buttonText,
                          trackingId,
                          "telegram_cta_click",
                          "telegram",
                          countryCode,
                          "BioLinkTemplate",
                        );
                        window.open(url, "_blank", "noopener,noreferrer");
                      },
                    },
                  });
                }}
                className="bio-link-button font-body w-full py-3 sm:py-4 px-5 bg-white text-black hover:bg-white/90 font-medium text-sm sm:text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 touch-manipulation cursor-pointer active:scale-[0.98] transition-transform"
              >
                <div className="flex items-center justify-center space-x-2">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                  </svg>
                  <span>
                    {templateConfig.isUSBio
                      ? "Join my Community"
                      : "Únete a mi Comunidad"}
                  </span>
                </div>
              </button>
            </div>

            {/* Social Links - Compact */}
            <div className="flex justify-center gap-3 sm:gap-4 flex-shrink-0">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    // Amplitude tracking removed
                    // Enhanced social tracking with country info
                    const socialCountryCode = templateConfig.country || "LATAM";
                    if (typeof window !== "undefined" && window.gtag) {
                      window.gtag("event", "social_click_country", {
                        event_category: "Social Media",
                        event_label: `${link.label}_${socialCountryCode}`,
                        country_code: socialCountryCode,
                        platform: link.label.toLowerCase(),
                        template: "BioLinkTemplate",
                        link_url: link.href,
                      });
                    }
                  }}
                  className="w-10 h-10 sm:w-11 sm:h-11 flex items-center justify-center rounded-xl bg-white/10 border border-white/20 text-white/80 hover:text-white hover:bg-white/20 shadow-lg touch-manipulation"
                  aria-label={link.label}
                >
                  {link.iconComponent ? (
                    <Suspense
                      fallback={<div className="w-6 h-6 sm:w-5 sm:h-5" />}
                    >
                      <link.iconComponent className="w-6 h-6 sm:w-5 sm:h-5" />
                    </Suspense>
                  ) : (
                    // TikTok SVG icon
                    <svg
                      className="w-6 h-6 sm:w-5 sm:h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                    </svg>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Elegant Footer - Transparent - Compact */}
        <footer className="py-4 px-4 text-center bg-transparent flex-shrink-0">
          <div className="flex flex-col items-center gap-2 sm:gap-3">
            {/* Social Media Icons - Compact */}
            <div className="flex items-center justify-center gap-3">
              <a
                href={BRAND_INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackContactMeta();
                  trackSocialClickPostHog(
                    "instagram",
                    BRAND_INSTAGRAM_URL,
                  );
                }}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-white/80 hover:text-white hover:bg-white/20 ui-transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <a
                href={BRAND_TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackContactMeta();
                  trackSocialClickPostHog(
                    "tiktok",
                    BRAND_TIKTOK_URL,
                  );
                }}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-white/80 hover:text-white hover:bg-white/20 ui-transition-colors duration-200"
                aria-label="TikTok"
              >
                <svg
                  className="w-4 h-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                </svg>
              </a>
            </div>
            {/* Copyright - Compact */}
            <p className="text-xs text-white/45 font-normal tracking-wide">
              {templateConfig.isUSBio
                ? `© ${new Date().getFullYear()} ${BRAND_PERSON_NAME || BRAND_NAME}`
                : `© ${new Date().getFullYear()} ${BRAND_PERSON_NAME || BRAND_NAME}`}
            </p>
          </div>
        </footer>
      </main>

      {/* Lightbox Modal */}
      {selectedMedia && (
        <div
          className={`fixed inset-0 bg-black z-50 flex items-center justify-center ${instagramOpts.cssClasses.modal}`}
          onClick={() => {
            if (selectedMedia && lightboxOpenTimeRef.current) {
              const timeSpent = Math.round(
                (Date.now() - lightboxOpenTimeRef.current) / 1000,
              );
              trackLightboxClosedPostHog(
                selectedMedia.id,
                timeSpent,
                lightboxViewedCountRef.current,
                "BioLinkTemplate",
                templateConfig.country || undefined,
              );
              lightboxOpenTimeRef.current = null;
            }
            setSelectedMedia(null);
          }}
        >
          {/* Close Button */}
          <button
            type="button"
            onClick={() => {
              if (selectedMedia && lightboxOpenTimeRef.current) {
                const timeSpent = Math.round(
                  (Date.now() - lightboxOpenTimeRef.current) / 1000,
                );
                trackLightboxClosedPostHog(
                  selectedMedia.id,
                  timeSpent,
                  lightboxViewedCountRef.current,
                  "BioLinkTemplate",
                  templateConfig.country || undefined,
                );
                lightboxOpenTimeRef.current = null;
              }
              setSelectedMedia(null);
            }}
            className="absolute top-6 right-6 sm:top-8 sm:right-8 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-white/90 hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-white/30"
            aria-label={templateConfig.isUSBio ? "Close" : "Cerrar"}
          >
            <X className="w-7 h-7" strokeWidth={2} />
          </button>

          {/* Navigation Buttons */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigateToPrevious();
            }}
            className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-white/90 hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-white/30"
            aria-label={templateConfig.isUSBio ? "Previous" : "Anterior"}
          >
            <ChevronLeft className="w-7 h-7" strokeWidth={2} />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigateToNext();
            }}
            className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center text-white/90 hover:text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors duration-200 z-20 focus:outline-none focus:ring-2 focus:ring-white/30"
            aria-label={templateConfig.isUSBio ? "Next" : "Siguiente"}
          >
            <ChevronRight className="w-7 h-7" strokeWidth={2} />
          </button>

          {/* Content with Instagram-like swipe gestures */}
          <div
            className="relative w-full h-full flex items-start justify-center overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{
              height: "100vh",
              maxHeight: "100vh",
              touchAction: instagramOpts.isInstagramBrowser
                ? "pan-y pinch-zoom"
                : "none",
            }}
            onTouchStart={(e) => {
              // Only handle swipe if not clicking on video player or action button
              const target = e.target as HTMLElement;
              if (
                target.closest("mux-player") ||
                target.closest(".action-button-container")
              ) {
                return; // Let video player handle its own touch events
              }

              const touch = e.touches[0];
              swipeStartRef.current = {
                x: touch.clientX,
                y: touch.clientY,
                time: Date.now(),
              };
            }}
            onTouchMove={(e) => {
              // Only prevent default scrolling during horizontal swipe for smooth navigation
              // This allows vertical scrolling in Instagram browser
              if (swipeStartRef.current) {
                const touch = e.touches[0];
                const deltaX = Math.abs(
                  touch.clientX - swipeStartRef.current.x,
                );
                const deltaY = Math.abs(
                  touch.clientY - swipeStartRef.current.y,
                );
                // Only prevent default if it's a horizontal swipe (reel-style navigation)
                // Allow vertical scrolling to work normally, especially in Instagram browser
                if (deltaX > deltaY && deltaX > 10) {
                  e.preventDefault();
                }
              }
            }}
            onTouchEnd={(e) => {
              if (!swipeStartRef.current) return;

              // Only handle swipe if not clicking on video player or action button
              const target = e.target as HTMLElement;
              if (
                target.closest("mux-player") ||
                target.closest(".action-button-container")
              ) {
                swipeStartRef.current = null;
                return; // Let video player handle its own touch events
              }

              const touch = e.changedTouches[0];
              const deltaX = touch.clientX - swipeStartRef.current.x;
              const deltaY = touch.clientY - swipeStartRef.current.y;
              const deltaTime = Date.now() - swipeStartRef.current.time;
              const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
              const velocity = distance / deltaTime;

              // Only process swipe if it's primarily horizontal and meets threshold
              // Lower threshold for Instagram browser to make navigation easier
              const swipeThreshold = instagramOpts.isInstagramBrowser
                ? SWIPE_THRESHOLD * 0.7
                : SWIPE_THRESHOLD;
              if (
                Math.abs(deltaX) > Math.abs(deltaY) &&
                Math.abs(deltaX) > swipeThreshold &&
                velocity > SWIPE_VELOCITY_THRESHOLD
              ) {
                if (deltaX > 0) {
                  // Swipe right - previous
                  navigateToPrevious();
                } else {
                  // Swipe left - next
                  navigateToNext();
                }
              }

              swipeStartRef.current = null;
            }}
          >
            {selectedMedia.type === "video" &&
            (selectedMedia.playbackId || selectedMedia.cloudflareId) ? (
              <div className="relative w-full h-full flex items-start justify-center bg-black">
                <Suspense
                  fallback={
                    <div className="w-full h-full flex items-center justify-center bg-black/20 rounded-lg">
                      <div className="text-white/60 text-sm">
                        Loading video...
                      </div>
                    </div>
                  }
                >
                  <MuxVideoSurface
                    playbackId={
                      selectedMedia.playbackId ||
                      selectedMedia.cloudflareId ||
                      ""
                    }
                    title={selectedMedia.title}
                    isVisible={true}
                    displayMode="reel"
                    posterFitMode="smartcrop"
                    autoplay={true} // Auto-play when opened
                    muted={false} // Play with sound
                    loop={true} // Loop for gallery experience
                    controls={false} // No controls for clean gallery
                    actionButton={selectedMedia.actionButton}
                    onPlay={() => {
                      videoProgressTrackedRef.current = new Set();
                      trackVideoPlayPostHog(
                        selectedMedia.id,
                        selectedMedia.title,
                        selectedMedia.playbackId || selectedMedia.cloudflareId,
                        "BioLinkTemplate",
                        templateConfig.country || undefined,
                      );
                    }}
                    onEnded={() => {
                      trackVideoCompletePostHog(
                        selectedMedia.id,
                        undefined,
                        "BioLinkTemplate",
                        templateConfig.country || undefined,
                      );
                    }}
                    onProgress={(percent, currentTimeSeconds) => {
                      if (videoProgressTrackedRef.current.has(percent)) return;
                      videoProgressTrackedRef.current.add(percent);
                      trackVideoProgressPostHog(
                        selectedMedia.id,
                        percent,
                        currentTimeSeconds,
                        "BioLinkTemplate",
                        templateConfig.country || undefined,
                      );
                    }}
                  />
                </Suspense>
              </div>
            ) : (
              <img
                src={selectedMedia.src}
                alt={`${selectedMedia.title} - Premium content by ${BRAND_PERSON_NAME} | Exclusive ${selectedMedia.type === "video" ? "video" : "photo"} content`}
                className="w-full h-full object-cover md:object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            )}
          </div>
        </div>
      )}

      {/* MuxVideoPlayerWithCTA - Fullscreen reel for Instagram / Join Telegram (no PaymentModal). Config-driven per key. */}
      {videoCTAOverlay.isOpen && videoCTAOverlay.config && (
        <Suspense
          fallback={
            <div className="fixed inset-0 bg-black z-[100] flex items-center justify-center">
              <div className="text-center space-y-4">
                <div
                  className="w-16 h-16 mx-auto border-2 border-white/30 border-t-white rounded-full animate-spin"
                  aria-hidden
                />
                <p className="text-white text-sm font-medium animate-pulse">
                  Cargando video...
                </p>
              </div>
            </div>
          }
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Video"
            className={`fixed inset-0 z-[100] w-full h-full min-h-dvh bg-black transition-opacity duration-200 ${
              videoCTAOverlayMounted ? "opacity-100" : "opacity-0"
            }`}
          >
            <MuxVideoPlayerWithCTA
              variant={videoCTAOverlay.config.variant ?? "fullscreen"}
              {...videoCTAOverlay.config}
              onClose={() =>
                setVideoCTAOverlay({ isOpen: false, config: null })
              }
              containerClassName="w-full h-full min-h-dvh"
            />
          </div>
        </Suspense>
      )}

    </div>
  );
};
